import { concat, EMPTY, from, fromEvent, Observable, of, timer } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import { forEach } from 'lodash';
import {
  ChatAction,
  ChatActions,
  ChatConnected,
  ChatConnectFailed,
  ChatStateChanged,
  ChatReconnectScheduled, ChatReconnectAttemptsExhausted,
  ConnectChat,
  DisconnectChat,
  LoadMessages, LoadMessagesFailed, LoadMessagesSuccess,
  MessageReceived,
  ReadMessages,
  SendMessage, SendMessageFailed, SendMessageSuccess,
  SubscribeChannel, UnsubscribeChannel,
  SyncChatChannels, SyncChatChannelsSuccess, SyncChatChannelsFailed,
  ChannelSubscriptionFailed,
} from '../actions/chat.action';
import ChatService from '../services/chat.service';
import { ChatMessage, mapJobToChatInfo } from '../../shared/models/chat';
import { pusherChatTransport } from '../services/pusherChatTransport';
import notificationSound from '../../assets/files/glympse-notification-sound.m4a';
import { getItem } from '../../shared/utils/local-storage-utils';
import { CHAT_SOUND_NOTIFICATIONS, GLYMPSE_PRO_CHAT_ORIGIN } from '../../shared/constants/chat.constants';
import { RootState } from '../../store/state';

const SOUND_NOTIFICATION = new Audio(notificationSound);

const connectChatEpic = (action$: Observable<ChatAction>, state$: Observable<RootState>) => action$.pipe(
  ofType<ConnectChat>(ChatActions.ConnectChat),
  withLatestFrom(state$),
  mergeMap(([, state]) => {
    const orgId = state?.profile?.userInfo?.orgId;
    return from(pusherChatTransport.connect(orgId)).pipe(
      mergeMap(() => of(
        new ChatConnected(),
        new ChatStateChanged({ previous: 'initialized', current: 'connected' }),
        new SyncChatChannels(),
      )),
      catchError((error) => of(
        new ChatConnectFailed((error as Error)?.message ?? 'Failed to connect'),
        new ChatStateChanged({ previous: 'initialized', current: 'disconnected' }),
      )),
    );
  }),
);

// Fetches the org's active chat channels (and seeds their message history)
// independently of the connect flow, so newly created/completed tasks are
// picked up without a full reconnect. Triggered once on connect and again
// in the background on each page navigation (see GeneralLayoutContent).
const syncChatChannelsEpic = (action$: Observable<ChatAction>, state$: Observable<RootState>) => action$.pipe(
  ofType<SyncChatChannels>(ChatActions.SyncChatChannels),
  withLatestFrom(state$),
  mergeMap(([, state]) => {
    const orgId = state?.profile?.userInfo?.orgId;
    const multiChatAccess = state?.orgHierarchy?.selectedOrg?.config?.agent?.multi_chat_access;
    const agentId = state?.profile?.userInfo?.account?.agent_id;
    return from(ChatService.getChatChannels(orgId, multiChatAccess === false ? agentId : undefined)).pipe(
      mergeMap((allTasks) => {
        // Tasks without a start_time haven't been started yet - skip them so
        // their chat isn't subscribed to or loaded until the task starts.
        const tasks = allTasks.filter((task) => !!task.start_time);
        const subscribeActions: ChatAction[] = tasks.map((task) => new SubscribeChannel({
          chat_channel: `${task.chat_channel}`,
          chatInfo: mapJobToChatInfo(task),
        }));
        const chatChannels = tasks.map((task) => `${task.chat_channel}`);
        // Dispatch subscribe actions immediately so chats/loading state show up
        // right away, rather than waiting on the slower messages batch below -
        // that gap is what let ChatView race ahead with its own fetch.
        return concat(
          of(...subscribeActions),
          from(ChatService.getMessagesForChannels(chatChannels)).pipe(
            catchError((error): Observable<Record<string, ChatMessage[]>> => {
              console.error('Failed to fetch chat messages:', error);
              return of({});
            }),
            mergeMap((messagesByChannel) => {
              const actions: ChatAction[] = [
                ...Object.keys(messagesByChannel).map((chatChannel) => new LoadMessagesSuccess({
                  chat_channel: chatChannel,
                  messages: messagesByChannel[chatChannel],
                })),
                new SyncChatChannelsSuccess(),
              ];
              return of(...actions);
            }),
          ),
        );
      }),
      catchError((error) => {
        console.error('Failed to sync chat channels:', error);
        return of(new SyncChatChannelsFailed((error as Error)?.message ?? 'Failed to sync chat channels'));
      }),
    );
  }),
);

// Backoff delay (ms) per reconnect attempt: 1s, 2s, 4s, 8s, 16s, then capped at 30s.
const RECONNECT_DELAYS_MS = [1000, 2000, 4000, 8000, 16000, 30000];
const MAX_RECONNECT_ATTEMPTS = 10;
const getReconnectDelay = (attempt: number) => RECONNECT_DELAYS_MS[Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)];

// Retries a failed connect with exponential backoff (capped at 30s), giving
// up after MAX_RECONNECT_ATTEMPTS. `reconnectAttempt` in state tracks how
// many retries have been scheduled so far and is reset on a successful connect.
const reconnectChatEpic = (action$: Observable<ChatAction>, state$: Observable<RootState>) => action$.pipe(
  ofType<ChatConnectFailed>(ChatActions.ChatConnectFailed),
  withLatestFrom(state$),
  mergeMap(([, state]) => {
    const attempt = state?.chat?.reconnectAttempt ?? 0;
    if (attempt >= MAX_RECONNECT_ATTEMPTS) {
      return of(new ChatReconnectAttemptsExhausted());
    }
    const delay = getReconnectDelay(attempt);
    return concat(
      of(new ChatReconnectScheduled({ attempt: attempt + 1, delay })),
      timer(delay).pipe(
        map(() => new ConnectChat()),
        takeUntil(action$.pipe(ofType(ChatActions.DisconnectChat))),
      ),
    );
  }),
);

// Re-syncs the chat channel list every 30s while connected, so tasks
// created/completed elsewhere are picked up without waiting for a page
// navigation (see GeneralLayoutContent for the navigation-triggered sync).
const periodicSyncChatChannelsEpic = (action$: Observable<ChatAction>) => action$.pipe(
  ofType<ChatConnected>(ChatActions.ChatConnected),
  switchMap(() => timer(30000, 30000).pipe(
    map(() => new SyncChatChannels()),
    takeUntil(action$.pipe(ofType(ChatActions.DisconnectChat))),
  )),
);

const disconnectChatEpic = (action$: Observable<ChatAction>, state$: Observable<RootState>) => action$.pipe(
  ofType<DisconnectChat>(ChatActions.DisconnectChat),
  withLatestFrom(state$),
  mergeMap(([, state]) => {
    forEach(state?.chat?.chats, (chat) => {
      pusherChatTransport.unsubscribe(chat?.chat_id);
    });
    pusherChatTransport.disconnect();
    // The DisconnectChat action's own reducer case already sets the correct
    // (non-reconnecting) state. Emitting a ChatStateChanged here too would
    // make the reducer re-derive `reconnecting` from `current: 'disconnected'`,
    // which is true for an intentional disconnect and would show a
    // never-ending "Reconnecting..." in the UI.
    return EMPTY;
  }),
);

const subscribeToChannelEpic = (action$: Observable<ChatAction>) => action$.pipe(
  ofType<SubscribeChannel>(ChatActions.SubscribeChannel),
  mergeMap((action) => {
    pusherChatTransport.subscribe(`${action.payload.chat_channel}`);
    return EMPTY;
  }),
);

const unsubscribeChannelEpic = (action$: Observable<ChatAction>) => action$.pipe(
  ofType<UnsubscribeChannel>(ChatActions.UnsubscribeChannel),
  mergeMap((action) => {
    pusherChatTransport.unsubscribe(`${action.payload}`);
    return EMPTY;
  }),
);

// Global listener for the transport. Every incoming
// message on any subscribed channel is routed into Redux as MessageReceived.
const chatTransportEpic = () => pusherChatTransport.onMessage().pipe(
  mergeMap((event) => new Observable<any>((observer) => {
    observer.next(new MessageReceived(event));
    if (event.message?.origin !== GLYMPSE_PRO_CHAT_ORIGIN) {
      const chatNotifications = getItem(CHAT_SOUND_NOTIFICATIONS);
      if (chatNotifications === 'true') {
        SOUND_NOTIFICATION.play();
      }
    }
  })),
);

// Surfaces per-channel Pusher subscription failures (e.g. auth rejected) into
// that chat's own state - the transport-level `connected` status alone
// wouldn't reflect this, since it only tracks the websocket connection.
const channelSubscriptionErrorEpic = () => pusherChatTransport.onSubscriptionError().pipe(
  map((event) => new ChannelSubscriptionFailed({
    chat_channel: event.chat_channel,
    // pusher-js emits { type: 'AuthError', error: '<message>' } - `error` is
    // a plain string here, not a nested Error object.
    error: event.error?.error ?? event.error?.message ?? 'Failed to subscribe to live updates for this chat',
  })),
);

// Keeps read/unread state in sync across browser tabs of the same session:
// ChatView writes the last-read sequence id to localStorage on read, which fires
// a native 'storage' event in every other tab. We listen for it here and clear
// the unread badge for that chat in all tabs.
const syncReadStateAcrossTabsEpic = () => fromEvent<StorageEvent>(window, 'storage').pipe(
  filter((event) => !!event.key?.startsWith('lastMessage_') && event.newValue !== event.oldValue),
  map((event) => new ReadMessages(`${event.key}`.replace('lastMessage_', ''))),
);

const loadMessagesEpic = (action$: Observable<ChatAction>) => action$.pipe(
  ofType<LoadMessages>(ChatActions.LoadMessages),
  mergeMap((action) => from(ChatService.loadMessages(action.payload)).pipe(
    mergeMap((response) => of(
      new LoadMessagesSuccess({
        chat_channel: action.payload,
        messages: response
      })
    )),
    catchError((error) => (
      of(new LoadMessagesFailed({
        error: (error as Error)?.message ?? 'Failed to load messages',
        chat_channel: action.payload,
      }))
    )),
  )),
);

const sendMessageEpic = (action$: Observable<ChatAction>) => action$.pipe(
  ofType<SendMessage>(ChatActions.SendMessage),
  mergeMap((action) => from(ChatService.sendMessage(action.payload.chat_channel, action.payload.message, action.payload.invite_code)).pipe(
    mergeMap((response) => of(new SendMessageSuccess({
      chat_channel: action.payload.chat_channel,
      // Merge over what we sent so the author name we attached client-side
      // survives even if the backend doesn't echo it back on the response.
      message: { ...action.payload.message, ...response }
    }))),
    catchError((error) => (
      of(new SendMessageFailed({
        error: (error as Error)?.message ?? 'Failed to send message',
        chat_channel: action.payload.chat_channel,
      }))
    )),
  )),
);

const ChatEpics = [
  connectChatEpic,
  reconnectChatEpic,
  syncChatChannelsEpic,
  periodicSyncChatChannelsEpic,
  disconnectChatEpic,
  subscribeToChannelEpic,
  unsubscribeChannelEpic,
  chatTransportEpic,
  channelSubscriptionErrorEpic,
  syncReadStateAcrossTabsEpic,
  loadMessagesEpic,
  sendMessageEpic
];

export default ChatEpics;
